import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Drawer, Empty, Popconfirm, Row, Spin, Table, Typography, Modal, Upload } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import AttributeService from '../../services/AttributeService';
import { listGroupsAll } from '../../modules/groups/services/groupService';
import { listInspectorsAll } from '../../modules/inspectors/services/inspectorService';
import { bulkUpdateAppointmentsByProposalAndAccount, getInspectionPlanByProposal } from '../../modules/inspection-appointments/services/inspection-appointmentService';
import ProposalService from '../../services/ProposalService';
import ControlAddressCard from './ControlAddressCard';
import { capitalizeSentences, openNotification } from '../../utils';
import { notificationTypeLabels, sortNotificationTypes } from '../../constants/notificationTypes';
import {
  getMatchingAuthorizedPersons,
  hasMatchingAuthorizedRecipients,
} from '../../utils/proposalAuthorizedRecipients';
import DocumentService from '../../services/DocumentService';
import PhoneNumberDisplay from '../PhoneNumberDisplay';
import LegalNotificationPlanTable, { LegalNotificationPlanTableRow } from './LegalNotificationPlanTable';

const { Text } = Typography;

interface CategoryListDrawerProps {
  open: boolean;
  proposalId: number | null;
  onClose: () => void;
  readOnly?: boolean;
}

interface PivotColumnMeta {
  key: string;
  name: string;
  sortOrder: number;
}

interface PivotRow {
  key: string;
  inspectorId: number;
  inspectorName: string;
  counts: Record<string, number>;
}

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif']);
const CONTRACT_ID_DEBOUNCE_MS = 600;

const extractFileNameFromUrl = (url: string): string => {
  const sanitized = url.split('?')[0] || '';
  const parts = sanitized.split('/');
  return decodeURIComponent(parts[parts.length - 1] || '');
};

const extractExtension = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const parts = value.split('.');
  if (parts.length <= 1) {
    return null;
  }
  return parts[parts.length - 1].toLowerCase();
};

const isImageLike = (value: string | null | undefined): boolean => {
  const ext = extractExtension(value);
  if (!ext) {
    return false;
  }
  return IMAGE_EXTENSIONS.has(ext);
};

const isImageUrl = (url: string | null | undefined): boolean => {
  if (!url) {
    return false;
  }
  const filename = extractFileNameFromUrl(url);
  return isImageLike(filename);
};

const minutesToDays = (minutes: number) => {
  if (!minutes || minutes <= 0) {
    return '0.00';
  }
  const days = minutes / 480;
  return days.toFixed(2);
};

const LEGAL_NOTIFICATION_WARNING_MESSAGE =
  'Teklif adresi ile eşleşen ve YASAL İŞLEMLER iletişim konusu seçili yetkili kişi bulunmamaktadır.';

const CategoryListDrawer: React.FC<CategoryListDrawerProps> = ({ open, proposalId, onClose, readOnly = false }) => {
  const isReadOnly = Boolean(readOnly);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attributes, setAttributes] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [inspectors, setInspectors] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [proposal, setProposal] = useState<any | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [currentContractUrl, setCurrentContractUrl] = useState<string | null>(null);
  const [currentContractName, setCurrentContractName] = useState<string | null>(null);
  const [currentContractIsImage, setCurrentContractIsImage] = useState(false);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string | null>(null);
  const [uploadPreviewIsImage, setUploadPreviewIsImage] = useState(false);
  const [contractIdDrafts, setContractIdDrafts] = useState<Record<number, string>>({});
  const [contractCancellationLoadingId, setContractCancellationLoadingId] = useState<number | null>(null);
  const [legalNotificationWarningShown, setLegalNotificationWarningShown] = useState(false);
  const contractIdUpdateTimers = useRef<Record<number, ReturnType<typeof setTimeout> | null>>({});
  const legalAuthorizedPersons = useMemo(() => {
    return getMatchingAuthorizedPersons(proposal, 'LEGAL');
  }, [proposal]);
  const legalAuthorizedPersonRows = useMemo(() => {
    return legalAuthorizedPersons.map((person: any, index: number) => ({
      ...person,
      key: String(person?.id ?? `legal-${index}`),
    }));
  }, [legalAuthorizedPersons]);
  const legalAuthorizedColumns = useMemo(() => [
    {
      title: 'Yetkili Kişi',
      key: 'person',
      render: (_: any, record: any) => {
        const firstName = record?.firstName?.toString().trim() || '';
        const lastName = record?.lastName?.toString().trim() || '';
        const fullName = `${firstName} ${lastName}`.trim() || 'İsimsiz Yetkili';
        const position =
          record?.position != null
            ? capitalizeSentences(String(record.position))
            : null;
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{fullName}</div>
            {position ? <Text type="secondary">{position}</Text> : null}
          </div>
        );
      },
    },
    {
      title: 'İletişim',
      key: 'contact',
      render: (_: any, record: any) => (
        <div>
          {record?.email ? (
            <a href={`mailto:${record.email}`} style={{ display: 'block' }}>
              {record.email}
            </a>
          ) : (
            <Text type="secondary">-</Text>
          )}
          <PhoneNumberDisplay value={record?.phoneNumber} />
        </div>
      ),
    },
    {
      title: 'İletişim Konuları',
      key: 'notificationTypes',
      render: (_: any, record: any) => {
        const types = Array.isArray(record?.notificationTypes)
          ? sortNotificationTypes(record.notificationTypes as any)
          : [];
        if (!types.length) {
          return <Text type="secondary">-</Text>;
        }
        const labels = types.map(
          (type) => notificationTypeLabels[type as keyof typeof notificationTypeLabels] ?? type,
        );
        return <span>{labels.join(', ')}</span>;
      },
    },
  ], []);

  useEffect(() => {
    if (!open || !proposalId) {
      if (!open) {
        setTasks([]);
        setError(null);
        setProposal(null);
        setLegalNotificationWarningShown(false);
      }
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [attrRes, inspectorData, groupData, planRes, proposalRes] = await Promise.all([
          AttributeService.getAll(),
          listInspectorsAll(),
          listGroupsAll('isActive=true'),
          getInspectionPlanByProposal(proposalId),
          ProposalService.get(proposalId),
        ]);

        if (cancelled) {
          return;
        }

        setAttributes(attrRes?.data || []);
        setInspectors(Array.isArray(inspectorData) ? inspectorData : []);
        setGroups(Array.isArray(groupData) ? groupData : []);
        const planTasks = Array.isArray(planRes?.tasks) ? planRes.tasks : [];
        setTasks(
          planTasks.map((task: any) => {
            const accountId = Number(task?.accountId ?? task?.account?.id ?? task?.inspectorId);
            return {
              ...task,
              accountId: Number.isNaN(accountId) ? undefined : accountId,
              inspectorId: Number.isNaN(accountId) ? task?.inspectorId : accountId,
            };
          }),
        );
        const planAppointments = Array.isArray(planRes?.appointments) ? planRes.appointments : [];
        setAppointments(
          planAppointments.map((appt: any) => {
            const accountId = Number(appt?.accountId ?? appt?.account?.id ?? appt?.inspectorId);
            return {
              ...appt,
              accountId: Number.isNaN(accountId) ? undefined : accountId,
              inspectorId: Number.isNaN(accountId) ? appt?.inspectorId : accountId,
            };
          }),
        );
        const proposalData = proposalRes?.data ?? proposalRes;
        setProposal(proposalData ?? null);
      } catch (err) {
        console.error('Yasal bildirim verileri alınırken hata oluştu:', err);
        if (!cancelled) {
          setError('Yasal bildirim yüklenemedi.');
          setAttributes([]);
          setInspectors([]);
          setGroups([]);
          setTasks([]);
          setProposal(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [open, proposalId]);

  useEffect(() => {
    return () => {
      Object.values(contractIdUpdateTimers.current).forEach((timer) => {
        if (timer) {
          clearTimeout(timer);
        }
      });
      contractIdUpdateTimers.current = {};
    };
  }, []);

  const hasLegalAuthorizedPerson = useCallback((record: any) => {
    if (!record) return false;
    return hasMatchingAuthorizedRecipients(record, 'LEGAL');
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!proposal) {
      return;
    }
    if (legalNotificationWarningShown) {
      return;
    }
    const hasLegal = hasLegalAuthorizedPerson(proposal);
    if (!hasLegal) {
      openNotification('warning', 'Uyarı', LEGAL_NOTIFICATION_WARNING_MESSAGE);
      setLegalNotificationWarningShown(true);
      onClose?.();
    }
  }, [open, proposal, hasLegalAuthorizedPerson, legalNotificationWarningShown, onClose]);

  const handleContractIdChange = useCallback(
    (inspectorId: number, value: string) => {
      if (isReadOnly) {
        return;
      }
      setContractIdDrafts((prev) => ({ ...prev, [inspectorId]: value }));

      const existingTimer = contractIdUpdateTimers.current[inspectorId];
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      if (!proposalId) {
        contractIdUpdateTimers.current[inspectorId] = null;
        return;
      }

      contractIdUpdateTimers.current[inspectorId] = setTimeout(async () => {
        try {
          const contractId = value.trim();
          await bulkUpdateAppointmentsByProposalAndAccount({ proposalId, accountId: inspectorId, contractId });
          setAppointments((prev) =>
            prev.map((a: any) =>
              Number(a?.accountId ?? a?.account?.id ?? a?.inspectorId) === inspectorId ? { ...a, contractId } : a,
            ),
          );
          setContractIdDrafts((prev) => {
            if (prev[inspectorId] === value) {
              return { ...prev, [inspectorId]: contractId };
            }
            return prev;
          });
        } catch (error) {
          console.error('Sözleşme numarası güncelleme hatası:', error);
          openNotification('error', 'Hata', 'Sözleşme numarası güncellenemedi.');
        } finally {
          contractIdUpdateTimers.current[inspectorId] = null;
        }
      }, CONTRACT_ID_DEBOUNCE_MS);
    },
    [proposalId, bulkUpdateAppointmentsByProposalAndAccount, openNotification, isReadOnly],
  );

  const handleContractCancellation = useCallback(
    async (inspectorId: number) => {
      if (isReadOnly || !proposalId) {
        return;
      }
      try {
        setContractCancellationLoadingId(inspectorId);
        await bulkUpdateAppointmentsByProposalAndAccount({
          proposalId,
          accountId: inspectorId,
          resetContract: true,
          notifyContractCancellation: true,
          isCompanyProcessDone: false,
          isInspectorProcessDone: false,
        });
        setAppointments((prev) =>
          prev.map((a: any) => {
            const accId = Number(a?.accountId ?? a?.account?.id ?? a?.inspectorId);
            if (accId === inspectorId) {
              return {
                ...a,
                contractUrl: null,
                contractId: null,
                isCompanyProcessDone: false,
                isInspectorProcessDone: false,
              };
            }
            return a;
          }),
        );
        setContractIdDrafts((prev) => {
          if (!(inspectorId in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[inspectorId];
          return next;
        });
        openNotification('success', 'Başarılı', 'Sözleşme iptal edildi ve ilgili uzmana SMS gönderildi.');
      } catch (error) {
        console.error('Sözleşme iptal hatası:', error);
        openNotification('error', 'Hata', 'Sözleşme iptal edilemedi.');
      } finally {
        setContractCancellationLoadingId(null);
      }
    },
    [isReadOnly, proposalId, openNotification, bulkUpdateAppointmentsByProposalAndAccount],
  );

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
    setUploading(false);
    setFileList([]);
    setUploadPreviewUrl(null);
    setUploadPreviewIsImage(false);
    setCurrentContractUrl(null);
    setCurrentContractName(null);
    setCurrentContractIsImage(false);
    setSelectedAccountId(null);
  };

  useEffect(() => {
    if (!fileList.length) {
      setUploadPreviewUrl(null);
      setUploadPreviewIsImage(false);
      return undefined;
    }
    const fileEntry = fileList[0];
    const originFile: any = fileEntry?.originFileObj ?? fileEntry;
    const fileName: string =
      originFile?.name ?? fileEntry?.name ?? fileEntry?.filename ?? '';
    const fileType: string = (originFile?.type ?? fileEntry?.type ?? '').toString();
    const isImage =
      (fileType && fileType.startsWith('image/')) || isImageLike(fileName);
    let objectUrl: string | null = null;

    if (isImage && originFile && typeof window !== 'undefined') {
      if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
        try {
          objectUrl = URL.createObjectURL(originFile);
          setUploadPreviewUrl(objectUrl);
        } catch (err) {
          setUploadPreviewUrl(null);
          objectUrl = null;
        }
      }
    } else {
      setUploadPreviewUrl(null);
    }

    setUploadPreviewIsImage(Boolean(isImage));

    return () => {
      if (objectUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileList]);

  const attributesById = useMemo(() => {
    const map = new Map<number, any>();
    attributes.forEach((attr: any) => {
      const id = Number(attr?.id);
      if (!Number.isNaN(id)) {
        map.set(id, attr);
      }
    });
    return map;
  }, [attributes]);

  const inspectorById = useMemo(() => {
    const map = new Map<number, any>();
    (inspectors || []).forEach((ins: any) => {
      const accountId = Number(ins?.account?.id);
      if (!Number.isNaN(accountId)) {
        map.set(accountId, ins);
        return;
      }
      const legacyId = Number(ins?.id);
      if (!Number.isNaN(legacyId)) {
        map.set(legacyId, ins);
      }
    });
    return map;
  }, [inspectors]);

  const pivotData = useMemo(() => {
    const groupMeta = new Map<string, PivotColumnMeta>();
    (groups || []).forEach((g: any) => {
      if (g && g.id != null) {
        groupMeta.set(`g_${g.id}`, {
          key: `g_${g.id}`,
          name: g.name || `Kategori #${g.id}`,
          sortOrder: typeof g.sortOrder === 'number' ? g.sortOrder : 999999,
        });
      }
    });

    const countsByInspector = new Map<number, PivotRow>();
    const effortsByInspector = new Map<number, Record<string, number>>();

    (tasks || []).forEach((task: any) => {
      const attributeId = Number(task?.attributeId ?? task?.attribute?.id);
      if (Number.isNaN(attributeId)) {
        return;
      }
      const meta = attributesById.get(attributeId) || {};
      const rawInspectorId =
        task?.accountId ?? task?.account?.id ?? task?.inspectorId ?? task?.inspector?.id;
      const inspectorId = Number(rawInspectorId);
      if (Number.isNaN(inspectorId) || inspectorId <= 0) {
        return;
      }

      const groupId = meta?.groupId ?? task?.groupId ?? null;
      const groupName = meta?.groupName ?? task?.groupName ?? 'Kategori Yok';
      const groupKey = groupId != null ? `g_${groupId}` : 'no-group';
      if (!groupMeta.has(groupKey)) {
        groupMeta.set(groupKey, {
          key: groupKey,
          name: groupName,
          sortOrder: groupKey === 'no-group' ? 9999999 : 999999,
        });
      }

      const inspectorData = inspectorById.get(inspectorId) || {};
      const firstName = inspectorData?.account?.firstName || '';
      const lastName = inspectorData?.account?.lastName || '';
      const inspectorNameFromDirectory = `${firstName} ${lastName}`.trim();
      const inspectorNameFromTask = (task?.inspectorName ?? task?.inspector?.fullName ?? '')
        .toString()
        .trim();
      const resolvedInspectorName = inspectorNameFromDirectory.length
        ? inspectorNameFromDirectory
        : inspectorNameFromTask.length
          ? inspectorNameFromTask
          : `#${inspectorId}`;

      const quantityRaw = task?.qty ?? task?.count ?? task?.quantity ?? task?.total ?? 0;
      const quantity = Number(quantityRaw);
      const safeQuantity = Number.isFinite(quantity) ? quantity : 0;
      const categoryQuantity =
        meta?.countable === false ? (safeQuantity > 0 ? 1 : 0) : safeQuantity;

      const existingRow = countsByInspector.get(inspectorId);
      if (existingRow) {
        existingRow.counts[groupKey] = (existingRow.counts[groupKey] || 0) + categoryQuantity;
        if (resolvedInspectorName && resolvedInspectorName !== `#${inspectorId}`) {
          existingRow.inspectorName = resolvedInspectorName;
        }
      } else {
        countsByInspector.set(inspectorId, {
          key: String(inspectorId),
          inspectorId,
          inspectorName: resolvedInspectorName,
          counts: { [groupKey]: categoryQuantity },
        });
      }

      const minutesPerUnit = Number(meta?.fieldControlTime) || 0;
      const addedMinutes = safeQuantity * minutesPerUnit;
      if (addedMinutes > 0) {
        const offeringId = meta?.offering?.id != null ? String(meta.offering.id) : 'no';
        const offeringName = meta?.offering?.name || 'Diğer';
        const effortKey = `${offeringId}::${offeringName}`;
        const currentEfforts = effortsByInspector.get(inspectorId) || {};
        currentEfforts[effortKey] = (currentEfforts[effortKey] || 0) + addedMinutes;
        effortsByInspector.set(inspectorId, currentEfforts);
      }
    });

    const sortedColumns = Array.from(groupMeta.values()).sort((a, b) => {
      const diff = (a.sortOrder ?? 999999) - (b.sortOrder ?? 999999);
      if (diff !== 0) {
        return diff;
      }
      return a.name.toString().localeCompare(b.name.toString(), 'tr');
    });

    const rows = Array.from(countsByInspector.values()).map((row) => {
      const data: any = {
        key: row.key,
        inspectorId: row.inspectorId,
        inspectorName: row.inspectorName,
      };
      sortedColumns.forEach((col) => {
        data[col.key] = row.counts[col.key] ?? 0;
      });
      return data;
    });

    return {
      columns: sortedColumns,
      rows,
      efforts: effortsByInspector,
    };
  }, [tasks, attributesById, groups, inspectorById]);

  const legalNotificationRows = useMemo<LegalNotificationPlanTableRow[]>(() => {
    const dynamicColumns = pivotData.columns.filter(
      (col) => col.key !== 'no-group' && (col.name || '').toString() !== 'Kategori Yok',
    );

    const buildInspectorContext = (record: any) => {
      const inspectorId = Number(record?.accountId ?? record?.account?.id ?? record?.inspectorId);
      const inspector = inspectorById.get(inspectorId) || {};
      const tc = inspector?.identityNumber || inspector?.account?.identityNumber;
      const inspectorAppointments = (appointments || []).filter((a: any) => Number(a?.accountId ?? a?.account?.id ?? a?.inspectorId) === inspectorId);
      let dateRangeText: string | null = null;
      if (inspectorAppointments.length > 0) {
        const starts: Dayjs[] = inspectorAppointments
          .map((a: any) => (a?.startDate ? dayjs(a.startDate) : null))
          .filter((d: Dayjs | null): d is Dayjs => Boolean(d) && d!.isValid());
        const ends: Dayjs[] = inspectorAppointments
          .map((a: any) => (a?.endDate ? dayjs(a.endDate) : null))
          .filter((d: Dayjs | null): d is Dayjs => Boolean(d) && d!.isValid());
        if (starts.length > 0 && ends.length > 0) {
          const latest = ends.reduce((max, d) => (d.isAfter(max) ? d : max));
          const latestPlus30 = latest.add(30, 'day');
          const tomorrow = dayjs().add(1, 'day');
          dateRangeText = `${tomorrow.format('DD/MM/YYYY')} - ${latestPlus30.format('DD/MM/YYYY')}`;
        }
      }
      const agg = {
        contractUrl: inspectorAppointments.find((a: any) => a?.contractUrl)?.contractUrl || null,
        contractId: inspectorAppointments.find((a: any) => a?.contractId)?.contractId || '',
        isCompanyProcessDone: inspectorAppointments.length > 0 && inspectorAppointments.every((a: any) => a?.isCompanyProcessDone === true),
        isInspectorProcessDone: inspectorAppointments.length > 0 && inspectorAppointments.every((a: any) => a?.isInspectorProcessDone === true),
      };
      const draft = contractIdDrafts[inspectorId] ?? agg.contractId ?? '';
      const effort = pivotData.efforts.get(inspectorId) || {};
      const effortParts = Object.entries(effort)
        .map(([key, minutes]) => {
          const [, offeringName] = key.split('::');
          const safeName = offeringName || 'Diğer';
          const totalMinutes = Number(minutes) || 0;
          if (totalMinutes <= 0) {
            return null;
          }
          return `${safeName}: ${minutesToDays(totalMinutes)} gün`;
        })
        .filter((value): value is string => Boolean(value))
        .sort((a, b) => a.localeCompare(b, 'tr'));

      const categories = dynamicColumns.map((col) => {
        const rawValue = record?.[col.key];
        const value = Number.isFinite(Number(rawValue)) ? Number(rawValue) : 0;
        return {
          key: col.key,
          name: col.name,
          value,
        };
      });

      return {
        inspectorId,
        inspectorName: record?.inspectorName,
        tc,
        dateRangeText,
        agg,
        draft,
        effortParts,
        categories,
      };
    };

    return pivotData.rows.map((record: any) => {
      const context = buildInspectorContext(record);
      const hasContractUrl = Boolean(context.agg.contractUrl);
      const hasContractId = Boolean(String(context.agg.contractId ?? '').trim());
      const cancellationInProgress = contractCancellationLoadingId === context.inspectorId;
      return {
        key: String(context.inspectorId),
        inspectorId: context.inspectorId,
        inspectorName: context.inspectorName,
        identityNumber: context.tc,
        dateRangeText: context.dateRangeText,
        effortParts: context.effortParts,
        contractId: context.agg.contractId,
        contractIdDraft: context.draft,
        contractUrl: context.agg.contractUrl,
        isCompanyProcessDone: context.agg.isCompanyProcessDone,
        isInspectorProcessDone: context.agg.isInspectorProcessDone,
        categories: context.categories,
        cancellationLoading: cancellationInProgress,
        showCancelContract: hasContractUrl || hasContractId,
      };
    });
  }, [
    pivotData,
    inspectorById,
    appointments,
    contractIdDrafts,
    contractCancellationLoadingId,
  ]);

  const content = useMemo(() => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
          <Spin />
        </div>
      );
    }

    if (error) {
      return <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} />;
    }

    if (!pivotData.rows.length) {
      return (
        <div style={{ marginTop: 16 }}>
          <Empty description={<Text>Henüz seçim yok.</Text>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      );
    }

    return (
      <LegalNotificationPlanTable
        rows={legalNotificationRows}
        readOnly={isReadOnly}
        onUploadClick={(row) => {
          setSelectedAccountId(row.inspectorId);
          const existingUrl = row.contractUrl ?? null;
          setCurrentContractUrl(existingUrl);
          setCurrentContractName(existingUrl ? extractFileNameFromUrl(existingUrl) : null);
          setCurrentContractIsImage(isImageUrl(existingUrl));
          setFileList([]);
          setUploadPreviewUrl(null);
          setUploadPreviewIsImage(false);
          setUploadModalOpen(true);
        }}
        onContractIdChange={(row, value) => handleContractIdChange(row.inspectorId, value)}
        onCompanyProcessChange={async (row, checked) => {
          if (isReadOnly || !proposalId) return;
          await bulkUpdateAppointmentsByProposalAndAccount({ proposalId, accountId: row.inspectorId, isCompanyProcessDone: checked });
          setAppointments((prev) =>
            prev.map((a: any) =>
              Number(a?.accountId ?? a?.account?.id ?? a?.inspectorId) === row.inspectorId ? { ...a, isCompanyProcessDone: checked } : a,
            ),
          );
        }}
        onInspectorProcessChange={async (row, checked) => {
          if (isReadOnly || !proposalId) return;
          await bulkUpdateAppointmentsByProposalAndAccount({ proposalId, accountId: row.inspectorId, isInspectorProcessDone: checked });
          setAppointments((prev) =>
            prev.map((a: any) =>
              Number(a?.accountId ?? a?.account?.id ?? a?.inspectorId) === row.inspectorId ? { ...a, isInspectorProcessDone: checked } : a,
            ),
          );
        }}
        onCancelContract={(row) => handleContractCancellation(row.inspectorId)}
      />
    );
  }, [loading, error, pivotData, legalNotificationRows, isReadOnly, proposalId, handleContractIdChange, handleContractCancellation]);

  const validateCompanyInformation = () => {
    const missingFields: string[] = [];

    const statusRaw = proposal?.statusId ?? proposal?.status?.id;
    const proposalStatusId = Number(statusRaw);
    const companyEmail = proposal?.company?.email;
    const sgkNumber = proposal?.address?.sgkNumber;
    const phone = proposal?.address?.phone;
    const controlAddressLine = proposal?.address?.addressLine1;

    if (!companyEmail || !String(companyEmail).trim()) {
      missingFields.push('Firma e-posta');
    }

    if (!sgkNumber || !String(sgkNumber).trim()) {
      missingFields.push('SGK/DETSİS numarası');
    }

    if (!phone || !String(phone).trim()) {
      missingFields.push('Telefon');
    }

    if (proposalStatusId === 7 && (!controlAddressLine || !String(controlAddressLine).trim())) {
      missingFields.push('Kontrol Adresi (Adres)');
    }

    if (missingFields.length > 0) {
      openNotification(
        'warning',
        'Eksik Firma Bilgisi',
        `Lütfen firma bilgilerini tamamlayın: ${missingFields.join(', ')}`,
        5,
      );
      return false;
    }

    return true;
  };

  const validateSubmissionReady = () => {
    const missing: string[] = [];

    pivotData.rows.forEach((row) => {
      const inspectorId = Number(row?.accountId ?? row?.account?.id ?? row?.inspectorId);
      if (!Number.isFinite(inspectorId)) {
        return;
      }
      const inspectorAppointments = (appointments || []).filter(
        (a: any) => Number(a?.accountId ?? a?.account?.id ?? a?.inspectorId) === inspectorId,
      );
      const contractUrl =
        inspectorAppointments.find((a: any) => a?.contractUrl)?.contractUrl ?? null;
      const contractIdRaw =
        inspectorAppointments.find((a: any) => a?.contractId)?.contractId ?? '';
      const contractId = String(contractIdRaw ?? '').trim();
      const isCompanyDone =
        inspectorAppointments.length > 0 &&
        inspectorAppointments.every((a: any) => a?.isCompanyProcessDone === true);
      const isInspectorDone =
        inspectorAppointments.length > 0 &&
        inspectorAppointments.every((a: any) => a?.isInspectorProcessDone === true);

      const missingParts: string[] = [];
      if (!contractUrl) {
        missingParts.push('sözleşme yüklenmeli');
      }
      if (!contractId) {
        missingParts.push('sözleşme numarası girilmeli');
      }
      if (!isCompanyDone) {
        missingParts.push('Firma süreci tamamlanmalı');
      }
      if (!isInspectorDone) {
        missingParts.push('Uzman süreci tamamlanmalı');
      }

      if (missingParts.length > 0) {
        const label =
          (row?.inspectorName || '').toString().trim() || `Uzman #${inspectorId}`;
        missing.push(`${label}: ${missingParts.join(', ')}`);
      }
    });

    if (missing.length > 0) {
      openNotification(
        'warning',
        'Eksik Bilgi',
        `Lütfen aşağıdaki uzmanlar için eksikleri tamamlayın: ${missing.join(' | ')}`,
        5,
      );
      return false;
    }

    return true;
  };

  const canCompleteLegalNotification = async (record: any) => {
    try {
      if (!record) return true;
      const hasLegal = hasLegalAuthorizedPerson(record);
      if (!hasLegal) {
        openNotification('warning', 'Uyarı', LEGAL_NOTIFICATION_WARNING_MESSAGE);
        return false;
      }
      return true;
    } catch (e) {
      return true;
    }
  };

  const handleCompleteLegal = async () => {
    if (!proposalId) return;
    try {
      await ProposalService.updateStatus(proposalId, 5);
      openNotification('success', 'Başarılı', 'Yasal bildirim işlemi tamamlandı.');
      onClose?.();
    } catch (error) {
      console.error('Yasal bildirim tamamlama sırasında hata oluştu:', error);
    }
  };

  return (
    <Drawer
      title="Yasal Bildirim"
      open={open}
      onClose={onClose}
      width={1200}
      footer={
        isReadOnly
          ? null
          : (
            <Row justify={"end"} >
              <Popconfirm
                placement="left"
                title="Yasal bildirim işlemini tamamlamak istediğinize emin misiniz?"
                onConfirm={async () => {
                  const companyInfoOk = validateCompanyInformation();
                  if (!companyInfoOk) {
                    return;
                  }
                  const ready = validateSubmissionReady();
                  if (!ready) {
                    return;
                  }
                  const ok = await canCompleteLegalNotification(proposal);
                  if (!ok) return;
                  await handleCompleteLegal();
                }}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button className="d-button dark-button width-auto" type="primary" >Kaydet ve Gönder</Button>
              </Popconfirm>
            </Row>
          )
      }
      destroyOnClose
    >
      <Modal
        title="Sözleşme Yükle"
        open={!isReadOnly && uploadModalOpen}
        onCancel={() => {
          handleCloseUploadModal();
        }}
        onOk={async () => {
          if (isReadOnly) return;
          if (!proposalId || !selectedAccountId || !fileList.length) return;
          try {
            setUploading(true);
            const fileObj = fileList[0]?.originFileObj || fileList[0];
            const uploaded = await DocumentService.postDocument(fileObj, 'appointment_contract');
            const docId = uploaded?.data?.id;
            let url: string | null = null;
            if (docId) {
              const res = await DocumentService.getDocument(docId);
              url = res?.data?.preSignUrl || res?.data?.preSignedUrl || null;
            }
            if (!url) {
              openNotification('error', 'Hata', 'Yükleme başarısız oldu.');
              setUploading(false);
              return;
            }
            await bulkUpdateAppointmentsByProposalAndAccount({ proposalId, accountId: selectedAccountId, contractUrl: url });
            setAppointments((prev) =>
              prev.map((a: any) =>
                Number(a?.accountId ?? a?.account?.id ?? a?.inspectorId) === selectedAccountId
                  ? { ...a, contractUrl: url }
                  : a,
              ),
            );
            openNotification('success', 'Başarılı', 'Sözleşme yüklendi.');
            handleCloseUploadModal();
          } catch (e) {
            console.error('Sözleşme yükleme hatası:', e);
            openNotification('error', 'Hata', 'Sözleşme yükleme başarısız.');
          } finally {
            setUploading(false);
          }
        }}
        okText="Yükle"
        okButtonProps={{ loading: uploading, disabled: !fileList.length }}
      >
        <div>
          {currentContractUrl ? (
            <div>
              <Text strong>Mevcut Sözleşme</Text>
              <div style={{ marginTop: 12 }}>
                {currentContractIsImage ? (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={currentContractUrl}
                      alt={currentContractName ?? 'Sözleşme'}
                      style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 8 }}
                    />
                  </div>
                ) : (
                  <Button
                    type="link"
                    href={currentContractUrl}
                    target="_blank"
                    download
                    style={{
                      padding: 0,
                      height: 'auto',
                      maxWidth: '100%',
                      whiteSpace: 'normal',
                      textAlign: 'left',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {currentContractName || 'Dosyayı indir'}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <Alert
              message="Henüz sözleşme yüklenmemiş."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <div style={{ marginTop: currentContractUrl ? 24 : 0 }}>
            <Text strong>Yeni Sözleşme</Text>
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              fileList={fileList}
              showUploadList={false}
              accept=".pdf,image/*"
              onChange={({ fileList: fl }) => setFileList(fl)}
            >
              <Button style={{ marginTop: 12 }}>Dosya Seç</Button>
            </Upload>

            {fileList.length > 0 ? (
              <div style={{ marginTop: 12 }}>
                {uploadPreviewIsImage && uploadPreviewUrl ? (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={uploadPreviewUrl}
                      alt="Seçilen sözleşme önizlemesi"
                      style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 8 }}
                    />
                  </div>
                ) : (
                  <Text>{fileList[0]?.name || 'Seçilen dosya'}</Text>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </Modal>
      <ControlAddressCard proposal={proposal} loading={loading} />
      <div style={{ marginTop: 16 }}>
        <Text strong>Yasal Bildirim Yetkilileri</Text>
        <Table
          style={{ marginTop: 12 }}
          dataSource={legalAuthorizedPersonRows}
          columns={legalAuthorizedColumns as any}
          pagination={false}
          size="small"
          rowKey="key"
          loading={loading}
          locale={{
            emptyText: loading ? <Spin /> : 'Yasal bildirim için yetkili kişi bulunamadı.',
          }}
        />
      </div>
      <div style={{ marginTop: 16 }}>
        {content}
      </div>
    </Drawer>
  );
};

export default CategoryListDrawer;
